import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { services } from '../../data/services';
import Logo from '../Logo';

const WaIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.858L.054 23.445a.75.75 0 00.922.922l5.587-1.479A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.7-.506-5.248-1.391l-.378-.22-3.917 1.036 1.036-3.917-.22-.378A9.952 9.952 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
  </svg>
);

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [disponibilite, setDisponibilite] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  useEffect(() => {
    fetch(`${API_URL}/api/disponibilite`)
      .then(r => r.json())
      .then(d => { if (d.label) setDisponibilite(d.label); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar */}
      <div className="bg-slate-800 text-slate-300 text-xs py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-6">
            {disponibilite ? (
              <span className="flex items-center gap-1.5 text-green-300">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                {disponibilite}
              </span>
            ) : (
              <span className="text-slate-400">Lun–Ven 8h–18h</span>
            )}
            <span className="text-slate-500">|</span>
            <span>Zone : Berry — Indre & Cher</span>
          </div>
          <div className="flex items-center gap-4">
            {/* RGE/Qualibat badges — à rétablir après obtention qualif
            <span className="flex items-center gap-1.5 bg-emerald-800/60 text-emerald-300 px-2.5 py-0.5 rounded-full font-medium">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
              Certifié RGE
            </span>
            <span className="flex items-center gap-1.5 bg-blue-800/60 text-blue-300 px-2.5 py-0.5 rounded-full font-medium">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
              Qualibat 9111
            </span>
            */}
            <span className="flex items-center gap-1.5 bg-purple-800/60 text-purple-300 px-2.5 py-0.5 rounded-full font-medium">
              <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
              Garantie décennale
            </span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={`bg-white transition-shadow duration-300 ${
          scrolled ? 'shadow-md' : 'shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center flex-shrink-0"
              onClick={() => { if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              <Logo />
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Services dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive('/services')
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-slate-700 hover:text-orange-600 hover:bg-slate-50'
                  }`}
                >
                  Nos Services
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {servicesOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[560px] bg-white rounded-2xl shadow-2xl border border-slate-100 py-4 z-50">
                    <div className="px-4 pb-3 border-b border-slate-100">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Toutes nos prestations
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 p-4">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-orange-50 hover:text-orange-700 transition-colors group"
                        >
                          <span className="text-xl flex-shrink-0">{s.icon}</span>
                          <div>
                            <p className="font-medium leading-tight group-hover:text-orange-700">
                              {s.name}
                            </p>
                            <p className="text-xs text-slate-400 leading-tight mt-0.5">
                              À partir de {s.price}€
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mx-4 mt-1 pt-3 border-t border-slate-100">
                      <Link
                        to="/services"
                        onClick={() => setServicesOpen(false)}
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-orange-50 text-orange-700 font-semibold rounded-xl hover:bg-orange-100 transition-colors text-sm"
                      >
                        Voir toutes nos prestations →
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {[
                { to: '/realisations', label: 'Réalisations' },
                { to: '/blog', label: 'Blog' },
                { to: '/a-propos', label: 'À Propos' },
                { to: '/contact', label: 'Contact' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-slate-700 hover:text-orange-600 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA + mobile toggle */}
            <div className="flex items-center gap-3">
              {/* L'équipe passe par là tous les jours ; les clients, jamais.
                  Présent mais discret, derrière les appels à l'action. */}
              <Link
                to="/pro/login"
                className="hidden lg:inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Connexion
              </Link>
              <Link
                to="/assistant"
                className="hidden xl:inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors border text-orange-600 border-orange-200 hover:bg-orange-50"
              >
                ✦ Assistant IA
              </Link>
              <Link
                to="/devis"
                className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all duration-200 text-sm"
              >
                Devis Gratuit
              </Link>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {mobileOpen ? (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 bg-white z-40 flex flex-col">
            <div className="bg-slate-800 px-4 py-2.5 flex items-center justify-between shrink-0">
              {disponibilite ? (
                <span className="flex items-center gap-1.5 text-green-300 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0 animate-pulse" />
                  {disponibilite}
                </span>
              ) : (
                <span className="text-slate-400 text-xs">Lun–Ven 8h–18h</span>
              )}
              <div className="flex items-center gap-2">
                {/* RGE/Qualibat badges — à rétablir après obtention qualif
                <span className="text-xs text-emerald-300 bg-emerald-800/60 px-2 py-0.5 rounded-full">
                  RGE
                </span>
                <span className="text-xs text-blue-300 bg-blue-800/60 px-2 py-0.5 rounded-full">
                  Qualibat
                </span>
                */}
                <span className="text-xs text-purple-300 bg-purple-800/60 px-2 py-0.5 rounded-full">
                  Garantie décennale
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-2">
                Nos Services
              </p>
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                >
                  <span className="text-xl">{s.icon}</span>
                  <span className="font-medium text-sm">{s.name}</span>
                </Link>
              ))}
              <div className="border-t border-slate-100 pt-3 mt-3 space-y-1">
                {[
                  { to: '/services', label: 'Toutes nos prestations' },
                  { to: '/realisations', label: 'Réalisations' },
                  { to: '/blog', label: 'Blog' },
                  { to: '/a-propos', label: 'À Propos' },
                  { to: '/contact', label: 'Contact' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive(link.to)
                        ? 'text-orange-600 bg-orange-50'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="pt-3 space-y-2">
                <a
                  href="https://wa.me/33759748656"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-3 rounded-lg transition-colors"
                >
                  <WaIcon className="w-4 h-4" />
                  WhatsApp
                </a>
                <Link
                  to="/devis"
                  className="block w-full text-center bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-3 rounded-lg transition-colors"
                >
                  Réaliser mon devis gratuit
                </Link>
                <Link
                  to="/pro/login"
                  className="block w-full text-center border border-slate-200 text-slate-600 font-semibold px-5 py-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Connexion
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
